import { useId, type FC, type RefObject } from "react";
import { PageOptions } from "../../types/page";
import dnaFooter from "../../assets/dna_footer.png";
import BrandAvatar from "../../components/BrandAvatar";
import VoiceModeIcon from "../../components/icons/VoiceModeIcon";
import PlusIcon from "../../components/icons/PlusIcon";
import { useTranslation } from "../../contexts/useTranslation";
import { useSupportAgents } from "../../contexts/useSupportAgents";
import Navbar from "../../components/Navbar";
import { useWidget } from "../../contexts/useWidget";
import { getResponseTimeTranslation, getSupportAgentNameTranslation } from "../../utils/translationUtil";
import Avatar from "../../components/Avatar";
import { sortUsersByAvatar } from "../../utils/userUtil";
import ConversationStarterButton from "./components/ConversationStarterButton";
import { Slot, Templated } from "../../templating";

interface LandingPageProps {
  containerRef: RefObject<HTMLDivElement>;
  navigate: (pageOptions: PageOptions) => void;
  isFullscreen: boolean;
}

const LandingPage: FC<LandingPageProps> = ({ containerRef, navigate, isFullscreen }) => {
  const { translate } = useTranslation();
  const { conversationStarters, averageResponseTime, theme } = useWidget();
  const { supportAgents } = useSupportAgents();
  const agentsToDisplay = sortUsersByAvatar(supportAgents).slice(0, 3);
  const hasDnaTheme = theme === "dna";
  const id = useId();

  return (
    <Templated id="lastbot-landing-page">
      <>
        <Slot name="navbar">
          <Navbar isFullscreen={isFullscreen} />
        </Slot>
        <div className="flex-1 flex flex-col gap-y-3 items-center justify-center text-center px-4 pt-4 overflow-y-auto">
          <Slot name="brand-avatar">
            <BrandAvatar />
          </Slot>
          <Slot name="welcome-text">
            <>
              <div className="text-3xl tracking-wide text-lastbot-on-surface">{translate("how_can_we_help")}</div>
              <div className="text-xs/6 text-lastbot-icon-muted">{translate("our_support_team_is_here_to_help_you")}</div>
            </>
          </Slot>
        </div>
        <div
          className={`flex flex-col gap-y-3 px-4 ${
            hasDnaTheme ? "pb-2" : "pb-8"
          } max-w-[490px] w-full self-center sm:px-8`}
        >
          {conversationStarters.length > 0 && (
            <Slot name="conversation-starters">
              <div className="flex gap-x-1 justify-center">
                {conversationStarters.map(([title, description], i) => (
                  <ConversationStarterButton
                    key={`${id}-${i}`}
                    containerRef={containerRef}
                    title={title}
                    description={description}
                    onClick={() => navigate({ page: { type: "conversation" }, pageContext: { message: description } })}
                  />
                ))}
              </div>
            </Slot>
          )}
          <Slot name="ask-anything-button">
            <button
              type="button"
              onClick={() => navigate({ page: { type: "customerInquiry" } })}
              className="flex items-center bg-lastbot-surface-muted rounded-full px-4 cursor-pointer"
              data-testid={`ask-anything`}
            >
              <span className="text-lastbot-icon-muted">
                <PlusIcon />
              </span>
              <span className="w-full px-4 py-4 text-lastbot-icon-muted text-left font-medium truncate">
                {translate("ask_anything")}
              </span>
              <span className="text-lastbot-icon-muted">
                <VoiceModeIcon />
              </span>
            </button>
          </Slot>
          <Slot name="agent-presence">
            <div className="flex items-center justify-center gap-x-2">
              {agentsToDisplay.length > 0 && (
                <div className="flex">
                  {agentsToDisplay.map((agent, i) => (
                    <Avatar
                      key={agent.id}
                      url={agent.avatar_url}
                      name={getSupportAgentNameTranslation(translate, agent)}
                      className={`${i > 0 ? "-ml-1.5" : ""}`}
                    />
                  ))}
                </div>
              )}
              <div className="text-xs/6 text-lastbot-icon-muted">{getResponseTimeTranslation(translate, averageResponseTime)}</div>
            </div>
          </Slot>
          {hasDnaTheme && <div className="dna-footer" style={{ backgroundImage: `url(${dnaFooter})` }}></div>}
        </div>
      </>
    </Templated>
  );
};

export default LandingPage;
